import React from 'react';
import { Mail } from 'lucide-react';
import { COPY } from '../../i18n/copy';
import { useI18n } from '../../i18n/useI18n';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error' | 'blocked';

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const DemoRequestSection: React.FC = () => {
  const { lang } = useI18n();
  const c = COPY[lang];

  const endpoint = 'https://formspree.io/f/mkozddnw';
  const method = 'POST';
  const contactEmail = 'sales@rekayasa.io';

  const [status, setStatus] = React.useState<FormStatus>(() => (!endpoint ? 'blocked' : 'idle'));
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const [fullName, setFullName] = React.useState('');
  const [workEmail, setWorkEmail] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [role, setRole] = React.useState('');
  const [interest, setInterest] = React.useState<'dashboard' | 'templates' | 'documents' | 'map' | 'notSure'>(
    'dashboard'
  );
  const [message, setMessage] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [website, setWebsite] = React.useState('');

  const [touched, setTouched] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    setStatus(!endpoint ? 'blocked' : 'idle');
  }, [endpoint]);

  const markTouched = (key: string) => setTouched((prev) => ({ ...prev, [key]: true }));

  const fullNameError =
    touched.fullName && !fullName.trim() ? c.demo.form.validation.required : '';
  const workEmailError =
    touched.workEmail && !workEmail.trim()
      ? c.demo.form.validation.required
      : touched.workEmail && workEmail.trim() && !isValidEmail(workEmail)
        ? c.demo.form.validation.invalidEmail
        : '';
  const companyError =
    touched.company && !company.trim() ? c.demo.form.validation.required : '';
  const consentError =
    touched.consent && !consent ? c.demo.form.validation.consentRequired : '';

  const canSubmit =
    status !== 'submitting' &&
    status !== 'blocked' &&
    !!fullName.trim() &&
    !!workEmail.trim() &&
    isValidEmail(workEmail) &&
    !!company.trim() &&
    consent;

  const mailto = React.useMemo(() => {
    const subject = encodeURIComponent('ERICA demo request');
    const body = encodeURIComponent(
      [
        `Name: ${fullName || '-'}`,
        `Email: ${workEmail || '-'}`,
        `Company: ${company || '-'}`,
        `Role: ${role || '-'}`,
        `Interest: ${interest}`,
        '',
        message ? `Message:\n${message}` : '',
      ]
        .filter(Boolean)
        .join('\n')
    );
    return `mailto:${contactEmail}?subject=${subject}&body=${body}`;
  }, [company, contactEmail, fullName, interest, message, role, workEmail]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setTouched({
      fullName: true,
      workEmail: true,
      company: true,
      consent: true,
    });

    if (!endpoint) {
      setStatus('blocked');
      return;
    }

    if (!canSubmit) return;

    if (website.trim()) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('fullName', fullName.trim());
      formData.append('workEmail', workEmail.trim());
      formData.append('company', company.trim());
      formData.append('role', role.trim());
      formData.append('interest', interest);
      formData.append('message', message.trim());
      formData.append('consent', consent ? 'true' : 'false');
      formData.append('lang', lang);
      formData.append('source', 'erica-landing');

      const res = await fetch(endpoint, {
        method,
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Request failed: ${res.status}`);
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : String(err));
    }
  };

  const onReset = () => {
    setStatus(!endpoint ? 'blocked' : 'idle');
    setErrorMessage('');
  };

  return (
    <section id="demo" className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {c.demo.title}
          </h2>
          <p className="mt-4 text-lg text-slate-600">{c.demo.subtitle}</p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              {status === 'blocked' ? (
                <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <div className="font-semibold text-slate-900">{c.demo.form.endpointMissingTitle}</div>
                  <div className="mt-1">{c.demo.form.endpointMissingBody}</div>
                </div>
              ) : null}

              {status === 'success' ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
                  <div className="text-base font-semibold text-slate-900">{c.demo.form.successTitle}</div>
                  <div className="mt-2">{c.demo.form.successBody}</div>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a
                      href={mailto}
                      className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                    >
                      {c.demo.form.emailUs}
                      <Mail className="ml-2 h-4 w-4" />
                    </a>
                    <button
                      type="button"
                      onClick={onReset}
                      className="inline-flex h-11 items-center justify-center rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:from-cyan-500 hover:to-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700"
                    >
                      {lang === 'id' ? 'Kirim permintaan lain' : 'Send another request'}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5">
                  <div className="hidden">
                    <label className="text-sm font-medium text-slate-900">
                      Website
                      <input
                        name="website"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        className="mt-2 block w-full rounded-xl border border-slate-200 px-3 py-2"
                      />
                    </label>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="text-sm font-medium text-slate-900">
                      {c.demo.form.fullName}
                      <input
                        name="fullName"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        onBlur={() => markTouched('fullName')}
                        className="mt-2 block h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none"
                        autoComplete="name"
                        required
                      />
                      {fullNameError ? <div className="mt-1 text-xs text-red-600">{fullNameError}</div> : null}
                    </label>
                    <label className="text-sm font-medium text-slate-900">
                      {c.demo.form.workEmail}
                      <input
                        name="workEmail"
                        type="email"
                        value={workEmail}
                        onChange={(e) => setWorkEmail(e.target.value)}
                        onBlur={() => markTouched('workEmail')}
                        className="mt-2 block h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none"
                        autoComplete="email"
                        required
                      />
                      {workEmailError ? <div className="mt-1 text-xs text-red-600">{workEmailError}</div> : null}
                    </label>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="text-sm font-medium text-slate-900">
                      {c.demo.form.company}
                      <input
                        name="company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        onBlur={() => markTouched('company')}
                        className="mt-2 block h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none"
                        autoComplete="organization"
                        required
                      />
                      {companyError ? <div className="mt-1 text-xs text-red-600">{companyError}</div> : null}
                    </label>
                    <label className="text-sm font-medium text-slate-900">
                      {c.demo.form.role}
                      <input
                        name="role"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="mt-2 block h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none"
                        autoComplete="organization-title"
                      />
                    </label>
                  </div>

                  <label className="text-sm font-medium text-slate-900">
                    {c.demo.form.interest}
                    <select
                      name="interest"
                      value={interest}
                      onChange={(e) => setInterest(e.target.value as typeof interest)}
                      className="mt-2 block h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none"
                    >
                      <option value="dashboard">{c.demo.form.interestOptions.dashboard}</option>
                      <option value="templates">{c.demo.form.interestOptions.templates}</option>
                      <option value="documents">{c.demo.form.interestOptions.documents}</option>
                      <option value="map">{c.demo.form.interestOptions.map}</option>
                      <option value="notSure">{c.demo.form.interestOptions.notSure}</option>
                    </select>
                  </label>

                  <label className="text-sm font-medium text-slate-900">
                    {c.demo.form.message}
                    <textarea
                      name="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      className="mt-2 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none"
                    />
                  </label>

                  <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    <input
                      name="consent"
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      onBlur={() => markTouched('consent')}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                      required
                    />
                    <span>
                      {c.demo.form.consent}
                      {consentError ? <div className="mt-1 text-xs text-red-600">{consentError}</div> : null}
                    </span>
                  </label>

                  {status === 'error' ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                      <div className="font-semibold text-red-800">{c.demo.form.errorTitle}</div>
                      <div className="mt-1">{c.demo.form.errorBody}</div>
                      {errorMessage ? (
                        <div className="mt-2 break-words text-xs text-red-700 opacity-80">
                          {errorMessage}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="submit"
                      disabled={status === 'blocked' || status === 'submitting'}
                      className="inline-flex h-11 items-center justify-center rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:from-cyan-500 hover:to-blue-500 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700"
                    >
                      {status === 'submitting' ? c.demo.form.submitting : c.demo.form.submit}
                    </button>
                    <a
                      href={mailto}
                      className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                    >
                      {c.demo.form.emailUs}
                      <Mail className="ml-2 h-4 w-4" />
                    </a>
                    {status === 'error' ? (
                      <button
                        type="button"
                        onClick={onReset}
                        className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-5 text-sm font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                      >
                        {c.demo.form.retry}
                      </button>
                    ) : null}
                  </div>
                </form>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">{c.demo.contactTitle}</div>
            <p className="mt-2 text-sm text-slate-600">{c.demo.contactBody}</p>
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-sm font-semibold text-slate-900">sales@rekayasa.io</div>
              <div className="mt-1 text-xs text-slate-500">
                {lang === 'id'
                  ? 'Kami akan membalas sesegera mungkin.'
                  : 'We’ll respond as soon as possible.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoRequestSection;
